<template>
  <div class="columns is-desktop mb-5">
    <!-- Primary Card -->
    <div class="column">
      <div class="card has-shadow has-border">
        <header class="card-header">
          <p class="card-header-title">
            {{ primaryTitle }}
          </p>
        </header>
        <div class="card-content">
          <transition name="fade" mode="out-in">
            <!-- Show empty state when no data is available -->
            <TableEmpty
              v-if="!hasValidPrimaryData"
              key="empty"
              text="Select from the dropdown above"
              size="small"
            />

            <!-- Show metrics when data is available -->
            <div v-else key="metrics" class="metrics-grid">
              <div class="metric-item">
                <p class="heading">stock count</p>
                <div
                  class="metric-row"
                  :class="{ centered: !getDiffPercentage('stockCount') }"
                >
                  <p class="metric-value" :class="getDiffClass('stockCount')">
                    {{ primaryMetrics.stockCount | numberFormat }}
                  </p>
                  <p
                    class="percentage-diff"
                    :class="getDiffClass('stockCount')"
                    v-if="getDiffPercentage('stockCount')"
                  >
                    <i class="fas" :class="getDiffIcon('stockCount')"></i>
                    {{ getDiffPercentage('stockCount') }}%
                  </p>
                </div>
              </div>
              <div class="metric-item">
                <p class="heading">average days in stock</p>
                <div
                  class="metric-row"
                  :class="{
                    centered: !getDiffPercentage('averageDaysInStock')
                  }"
                >
                  <p
                    class="metric-value"
                    :class="getDiffClass('averageDaysInStock')"
                  >
                    {{ primaryMetrics.averageDaysInStock }}
                  </p>
                  <p
                    class="percentage-diff"
                    :class="getDiffClass('averageDaysInStock')"
                    v-if="getDiffPercentage('averageDaysInStock')"
                  >
                    <i
                      class="fas"
                      :class="getDiffIcon('averageDaysInStock')"
                    ></i>
                    {{ getDiffPercentage('averageDaysInStock') }}%
                  </p>
                </div>
              </div>
              <div class="metric-item">
                <p class="heading">average price</p>
                <div
                  class="metric-row"
                  :class="{ centered: !getDiffPercentage('averagePrice') }"
                >
                  <p class="metric-value" :class="getDiffClass('averagePrice')">
                    {{ primaryMetrics.averagePrice | currencyFormat }}
                  </p>
                  <p
                    class="percentage-diff"
                    :class="getDiffClass('averagePrice')"
                    v-if="getDiffPercentage('averagePrice')"
                  >
                    <i class="fas" :class="getDiffIcon('averagePrice')"></i>
                    {{ getDiffPercentage('averagePrice') }}%
                  </p>
                </div>
              </div>
              <div class="metric-item">
                <p class="heading">average market demand</p>
                <div
                  class="metric-row"
                  :class="{
                    centered: !getDiffPercentage('averageMarketDemand')
                  }"
                >
                  <p
                    class="metric-value"
                    :class="getDiffClass('averageMarketDemand')"
                  >
                    {{ primaryMetrics.averageMarketDemand }}
                  </p>
                  <p
                    class="percentage-diff"
                    :class="getDiffClass('averageMarketDemand')"
                    v-if="getDiffPercentage('averageMarketDemand')"
                  >
                    <i
                      class="fas"
                      :class="getDiffIcon('averageMarketDemand')"
                    ></i>
                    {{ getDiffPercentage('averageMarketDemand') }}%
                  </p>
                </div>
              </div>
              <div class="metric-item">
                <p class="heading">sold last 30 days</p>
                <div
                  class="metric-row"
                  :class="{ centered: !getDiffPercentage('soldLast30Days') }"
                >
                  <p
                    class="metric-value"
                    :class="getDiffClass('soldLast30Days')"
                  >
                    {{ primaryMetrics.soldLast30Days }}
                  </p>
                  <p
                    class="percentage-diff"
                    :class="getDiffClass('soldLast30Days')"
                    v-if="getDiffPercentage('soldLast30Days')"
                  >
                    <i class="fas" :class="getDiffIcon('soldLast30Days')"></i>
                    {{ getDiffPercentage('soldLast30Days') }}%
                  </p>
                </div>
              </div>
              <div class="metric-item">
                <p class="heading">average mileage</p>
                <div
                  class="metric-row"
                  :class="{ centered: !getDiffPercentage('averageMileage') }"
                >
                  <p
                    class="metric-value"
                    :class="getDiffClass('averageMileage')"
                  >
                    {{ primaryMetrics.averageMileage | numberFormat }}
                  </p>
                  <p
                    class="percentage-diff"
                    :class="getDiffClass('averageMileage')"
                    v-if="getDiffPercentage('averageMileage')"
                  >
                    <i class="fas" :class="getDiffIcon('averageMileage')"></i>
                    {{ getDiffPercentage('averageMileage') }}%
                  </p>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <!-- Competitors Card -->
    <div class="column">
      <div class="card has-shadow has-border">
        <header class="card-header">
          <p class="card-header-title">
            {{ competitorTitle }}
          </p>
        </header>
        <div class="card-content">
          <transition name="fade" mode="out-in">
            <!-- Show empty state when no data is available -->
            <TableEmpty
              v-if="!hasValidCompetitorData"
              key="empty"
              text="Select competitors above"
              size="small"
            />

            <!-- Show metrics when data is available -->
            <div v-else key="metrics" class="metrics-grid">
              <div class="metric-item">
                <p class="heading">stock count</p>
                <p class="metric-value">
                  {{ competitorMetrics.stockCount | numberFormat }}
                </p>
              </div>
              <div class="metric-item">
                <p class="heading">average days in stock</p>
                <p class="metric-value">
                  {{ competitorMetrics.averageDaysInStock }}
                </p>
              </div>
              <div class="metric-item">
                <p class="heading">average price</p>
                <p class="metric-value">
                  {{ competitorMetrics.averagePrice | currencyFormat }}
                </p>
              </div>
              <div class="metric-item">
                <p class="heading">average market demand</p>
                <p class="metric-value">
                  {{ competitorMetrics.averageMarketDemand }}
                </p>
              </div>
              <div class="metric-item">
                <p class="heading">sold last 30 days</p>
                <p class="metric-value">
                  {{ competitorMetrics.soldLast30Days }}
                </p>
              </div>
              <div class="metric-item">
                <p class="heading">average mileage</p>
                <p class="metric-value">
                  {{ competitorMetrics.averageMileage | numberFormat }}
                </p>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { mockData } from '../mockData/stockCompareChartsMockData'
import TableEmpty from 'core/components/TableEmpty'

export default {
  name: 'StockCompareMetricsTable',
  components: {
    TableEmpty
  },
  computed: {
    ...mapGetters('stockcompare/dashboard', [
      'primaryStockMetrics',
      'competitorStockMetrics',
      'myMakes',
      'competitorMakes'
    ]),

    primaryMetrics() {
      if (this.primaryStockMetrics) {
        return this.primaryStockMetrics
      }

      const stockCount = this.calculateStockCount(this.myMakes)
      const averageDaysInStock = this.calculateWeightedAverage(
        this.myMakes,
        'averageDaysInStock'
      )
      const soldLast30Days = this.calculateSum(this.myMakes, 'soldLast30Days')
      const averageMarketDemand = this.calculateWeightedAverage(
        this.myMakes,
        'averageMarketDemand'
      )
      const averagePrice = this.calculateWeightedAverage(
        this.myMakes,
        'averagePrice'
      )
      const averageMileage = this.calculateWeightedAverage(
        this.myMakes,
        'averageMileage'
      )

      return {
        stockCount: stockCount,
        averageDaysInStock,
        averagePrice,
        averageMarketDemand,
        soldLast30Days,
        averageMileage
      }
    },

    competitorMetrics() {
      if (this.competitorStockMetrics) {
        return this.competitorStockMetrics
      }

      const stockCount = this.calculateStockCount(this.competitorMakes)
      const averageDaysInStock = this.calculateWeightedAverage(
        this.competitorMakes,
        'averageDaysInStock'
      )
      const soldLast30Days = this.calculateSum(
        this.competitorMakes,
        'soldLast30Days'
      )
      const averageMarketDemand = this.calculateWeightedAverage(
        this.competitorMakes,
        'averageMarketDemand'
      )
      const averagePrice = this.calculateWeightedAverage(
        this.competitorMakes,
        'averagePrice'
      )
      const averageMileage = this.calculateWeightedAverage(
        this.competitorMakes,
        'averageMileage'
      )

      return {
        stockCount: stockCount,
        averageDaysInStock,
        averagePrice,
        averageMarketDemand,
        soldLast30Days,
        averageMileage
      }
    },

    primaryTitle() {
      if (!this.myMakes || this.myMakes.length === 0) {
        return 'Select from the dropdown above'
      }

      const makeNames = this.myMakes.map(make => {
        return make.name || make.make || make
      })

      return makeNames.join(' & ')
    },

    competitorTitle() {
      if (!this.competitorMakes || this.competitorMakes.length === 0) {
        return 'Select competitors above'
      }

      const makeNames = this.competitorMakes.map(make => {
        return make.name || make.make || make
      })

      return makeNames.join(' & ')
    },

    hasValidPrimaryData() {
      return this.myMakes && this.myMakes.length > 0
    },

    hasValidCompetitorData() {
      return this.competitorMakes && this.competitorMakes.length > 0
    }
  },
  methods: {
    calculateStockCount(makes) {
      if (!makes || makes.length === 0) {
        return 'No data'
      }

      let totalStock = 0
      let foundValidMake = false

      makes.forEach(make => {
        const makeName = (make.name || make.make || make || '')
          .toLowerCase()
          .trim()

        // Search dynamically through all available data
        const brandKey = this.findBrandKey(makeName)

        if (brandKey && mockData[brandKey]) {
          totalStock += mockData[brandKey].stockTotal
          foundValidMake = true
        }
      })

      return foundValidMake ? totalStock : 'No data'
    },

    findBrandKey(makeName) {
      if (!makeName) return null

      const normalizedMakeName = makeName.toLowerCase().trim()
      // Search for exact match first
      const exactMatch = Object.keys(mockData).find(
        key => key.toLowerCase() === normalizedMakeName
      )
      if (exactMatch) return exactMatch

      // Search for partial match (makeName contains brandKey or vice versa)
      const partialMatch = Object.keys(mockData).find(key => {
        const keyLower = key.toLowerCase()
        return (
          normalizedMakeName.includes(keyLower) ||
          keyLower.includes(normalizedMakeName)
        )
      })
      if (partialMatch) return partialMatch

      // Search for word match (for camelCase like buildYourDreams)
      const wordMatch = Object.keys(mockData).find(key => {
        const spacedKey = key
          .replace(/([A-Z])/g, ' $1')
          .toLowerCase()
          .trim()
        return (
          normalizedMakeName.includes(spacedKey) ||
          spacedKey.includes(normalizedMakeName)
        )
      })

      return wordMatch || null
    },

    calculateWeightedAverage(makes, metric) {
      if (!makes || makes.length === 0) {
        return 'No data'
      }

      let totalWeightedValue = 0
      let totalWeight = 0
      let foundValidMake = false

      makes.forEach(make => {
        const makeName = (make.name || make.make || make || '')
          .toLowerCase()
          .trim()

        // Search dynamically through all available data
        const brandKey = this.findBrandKey(makeName)

        if (brandKey && mockData[brandKey]) {
          const metricValue = mockData[brandKey][metric]
          const weight = mockData[brandKey].stockTotal

          if (metricValue !== undefined && weight !== undefined) {
            totalWeightedValue += metricValue * weight
            totalWeight += weight
            foundValidMake = true
          }
        }
      })

      if (!foundValidMake || totalWeight === 0) {
        return 'No data'
      }

      const weightedAverage = totalWeightedValue / totalWeight

      // For price and mileage metrics, return rounded integer (no decimals)
      if (metric === 'averagePrice' || metric === 'averageMileage') {
        return Math.round(weightedAverage)
      }

      // For decimal metrics like averageDaysInStock and averageMarketDemand, return with 1 decimal
      return parseFloat(weightedAverage.toFixed(1))
    },

    calculateSum(makes, metric) {
      if (!makes || makes.length === 0) {
        return 'No data'
      }

      let totalSum = 0
      let foundValidMake = false

      makes.forEach(make => {
        const makeName = (make.name || make.make || make || '')
          .toLowerCase()
          .trim()

        // Search dynamically through all available data
        const brandKey = this.findBrandKey(makeName)

        if (brandKey && mockData[brandKey]) {
          const metricValue = mockData[brandKey][metric]

          if (metricValue !== undefined) {
            totalSum += metricValue
            foundValidMake = true
          }
        }
      })

      return foundValidMake ? totalSum : 'No data'
    },

    getDiffPercentage(metric) {
      const primary = this.primaryMetrics[metric]
      const competitor = this.competitorMetrics[metric]

      // If any is No data or doesn't exist, don't show percentage
      if (
        !primary ||
        !competitor ||
        primary === 'No data' ||
        competitor === 'No data'
      )
        return ''

      const diff = ((primary - competitor) / competitor) * 100
      return Math.abs(diff).toFixed(1)
    },

    getDiffClass(metric) {
      const primary = this.primaryMetrics[metric]
      const competitor = this.competitorMetrics[metric]

      // If any is No data or doesn't exist, don't apply class
      if (
        !primary ||
        !competitor ||
        primary === 'No data' ||
        competitor === 'No data'
      )
        return ''

      // If values are equal (0% difference), always return neutral-down (blue)
      if (primary === competitor) {
        return 'neutral-down'
      }

      const higherIsBetter = ['soldLast30Days', 'averageMarketDemand']
      const lowerIsBetter = [
        'averageDaysInStock',
        'averagePrice',
        'averageMileage'
      ]

      const isHigher = primary > competitor

      if (higherIsBetter.includes(metric)) {
        return isHigher ? 'positive' : 'negative'
      } else if (lowerIsBetter.includes(metric)) {
        return isHigher ? 'negative' : 'positive'
      } else {
        // For stock count, neutral
        return isHigher ? 'neutral-up' : 'neutral-down'
      }
    },

    getDiffIcon(metric) {
      const primary = this.primaryMetrics[metric]
      const competitor = this.competitorMetrics[metric]

      // If any is No data or doesn't exist, don't show icon
      if (
        !primary ||
        !competitor ||
        primary === 'No data' ||
        competitor === 'No data'
      )
        return ''

      // If values are equal (0% difference), show equals sign
      if (primary === competitor) {
        return 'fa-equals'
      }

      return primary > competitor ? 'fa-arrow-up' : 'fa-arrow-down'
    }
  },
  filters: {
    numberFormat(value) {
      if (!value || value === 'No data') return value || 'No data'
      return value.toLocaleString()
    },
    currencyFormat(value) {
      if (!value || value === 'No data') return value || 'No data'
      return '£' + value.toLocaleString()
    }
  }
}
</script>

<style scoped>
.card {
  height: 100%;
}

.card .card-header {
  background: white;
  border-bottom: 1px solid #e5e5e5;
}

.card .card-header .card-header-title {
  color: #454444;
  font-weight: 600;
}

.card .card-content {
  padding: 1.5rem;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  align-items: start;
}

.metric-item {
  text-align: center;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.metric-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.metric-row.centered {
  justify-content: center;
}

.heading {
  font-size: 0.9rem;
  color: #757575;
  text-transform: lowercase;
  font-weight: 400;
  line-height: 1.2;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: normal;
}

.metric-value {
  font-size: 2.2rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
}

.metric-value {
  color: #626262;
}

.percentage-diff {
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  margin-left: 0.5rem;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  min-width: 50px;
  color: white;
}

.percentage-diff.positive {
  background-color: #65bb2d;
}

.percentage-diff.negative {
  background-color: #ff3860;
}

.percentage-diff.neutral-up {
  background-color: #65bb2d;
}

.percentage-diff.neutral-down {
  background-color: #3273dc;
}

.percentage-diff i {
  font-size: 0.7rem;
}

.percentage-diff i.fa-equals {
  font-size: 0.5rem;
}

.card.has-shadow {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.card.has-border {
  border: 2px solid rgb(228, 228, 228);
}

/* Fade transition styles */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
